/**
 * IntegrationListingInsights
 *
 * Summary metrics bar for the IntegrationListing page.
 * Consumes server-computed aggregates from GET /bpa/integrations/insights/
 * via `integrationInsightsAtom` (populated by `useIntegrationInsights`).
 *
 * Displayed metrics:
 *  - Active / Aging / Aged-out bar (SegmentedBar)
 *  - Total Integrations
 *  - Active count
 *  - Aging count
 *  - Aged Out count
 *  - Providers
 *  - Modules
 *  - Regions
 */

import InsightItem from 'components/InsightItem';
import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';
import '../../styles.css';

import { useIntegrationInsights } from '../../hooks/useIntegrationInsights';
import { integrationInsightsAtom } from '../../state';

const IntegrationListingInsights: React.FC = () => {
  // Register the side-effect hook that fetches and populates the atom
  useIntegrationInsights();

  const insights = useAtomValue(integrationInsightsAtom);
  const { loading } = insights;

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card className="integration-listing-insights-card mb-0 px-3 py-1">
        <div className="d-flex flex-wrap align-items-center">
          {/* Status bar */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Integration Status:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'pass',
                  value: insights.active_integrations,
                  label: 'Active',
                },
                {
                  color: 'fail',
                  value: insights.aged_out_integration,
                  label: 'Aged Out',
                },
              ]}
              labelDisplay="value"
              tooltip={`Active: ${insights.active_integrations}, Aging: ${insights.aging_integrations}, Aged Out: ${insights.aged_out_integration}`}
            />
          </div>

          <InsightItem
            label="Integrations"
            value={loading ? 0 : insights.total_integrations}
            tooltip="Total integrations"
          />
          <InsightItem
            label="Providers"
            value={loading ? 0 : insights.provider_count}
            tooltip="Unique cloud providers"
          />
          <InsightItem
            label="Modules"
            value={loading ? 0 : insights.module_count}
            tooltip="Unique modules"
          />
          <InsightItem
            label="Regions"
            value={loading ? 0 : insights.region_count}
            tooltip="Unique regions"
            isLast
          />
        </div>
      </Card>
    </div>
  );
};

export default IntegrationListingInsights;
